import {
  type QuickStoryScore,
  type QuickStoryScoreSource,
} from "#customTypes/composedTypes/standoutTypes"

/** Display bands. Keep these aligned with QUICK_STORY_SCORE_RUBRIC. */
export const QUICK_STORY_SCORE_HIGH = 90
export const QUICK_STORY_SCORE_MID = 50

export type QuickStoryScoreBand = "high" | "mid" | "low"

export const quickStoryNote = (note: string | undefined): string =>
  note?.trim() ?? ""

export const quickStoryScoreMatches = (
  score: QuickStoryScore | undefined,
  note: string
): score is QuickStoryScore => !!score && score.scoredNote === note

/**
 * Score a non-secret note that does not already have a score for this
 * exact text. A sent moment is left alone until someone edits the note,
 * so opening the page does not score the whole history.
 */
export type QuickStoryScorePhase = "hidden" | "scoring" | "ready" | "failed"

/**
 * What the card shows. A failed request only offers retry while a score is
 * still wanted. Once the moment is sent, `request` is false, and a leftover
 * failure must not leave "retry score" on a story this pass does not score.
 */
export const quickStoryScorePhase = (input: {
  note: string
  category?: string
  fresh: boolean
  failed: boolean
  request: boolean
}): QuickStoryScorePhase => {
  if (!input.note || input.category === "secret") return "hidden"
  if (input.fresh) return "ready"
  if (input.failed && input.request) return "failed"
  if (input.request) return "scoring"
  return "hidden"
}

export const shouldRequestQuickStoryScore = (input: {
  note?: string
  category?: string
  bucket: "pending" | "auto" | "sent" | "undeliverable"
  score?: QuickStoryScore
}): boolean => {
  const note = quickStoryNote(input.note)
  if (!note) return false
  if (input.category === "secret") return false
  if (quickStoryScoreMatches(input.score, note)) return false
  if (!input.score && input.bucket === "sent") return false
  return true
}

export const quickStoryScoreBand = (
  confidence: number
): QuickStoryScoreBand => {
  if (confidence >= QUICK_STORY_SCORE_HIGH) return "high"
  if (confidence >= QUICK_STORY_SCORE_MID) return "mid"
  return "low"
}

export const quickStoryScoreTitle = (
  score: QuickStoryScore,
  note?: string
): string => {
  const snippet = note?.trim() ?? ""
  const lines = [
    score.source === "stand-in" ? "Emulator stand-in, not the model." : "",
    `${score.confidence} / 100 confidence this is safe to auto-approve`,
    `Positive vibe: ${score.positive ? "yes" : "no"}`,
    `Appropriate: ${score.appropriate ? "yes" : "no"}`,
    score.reason,
    snippet
      ? snippet.length > 80
        ? `${snippet.slice(0, 77)}...`
        : snippet
      : "",
  ]
  return lines.filter((line) => line.length > 0).join("\n")
}

export const isQuickStoryScoreSource = (
  value: unknown
): value is QuickStoryScoreSource => value === "model" || value === "stand-in"
